import React, { useMemo, useState } from "react";
import { localized, useLanguage } from "@/i18n";

const TAGS = [
  { id: "All", labelKey: "all" },
  { id: "Sketches", labelKey: "sketches" },
  { id: "Models", labelKey: "models" },
  { id: "Research", labelKey: "research" },
  { id: "Visuals", labelKey: "visuals" },
  { id: "Academic", labelKey: "academic" },
  { id: "Experiments", labelKey: "experiments" },
];

const beanAndLeafImages = [
  `${process.env.PUBLIC_URL}/assets/projects/bean-and-leaf/25.png`,
  `${process.env.PUBLIC_URL}/assets/projects/bean-and-leaf/26.png`,
  `${process.env.PUBLIC_URL}/assets/projects/bean-and-leaf/210.png`,
];

const artistHubImages = [
  `${process.env.PUBLIC_URL}/assets/projects/artist-hub/313.png`,
  `${process.env.PUBLIC_URL}/assets/projects/artist-hub/312.png`,
  `${process.env.PUBLIC_URL}/assets/projects/artist-hub/3.png`,
];

const netZeroCityImages = [
  `${process.env.PUBLIC_URL}/assets/projects/net-zero-city/346.png`,
  `${process.env.PUBLIC_URL}/assets/projects/net-zero-city/336.png`,
  `${process.env.PUBLIC_URL}/assets/projects/net-zero-city/328.png`,
  `${process.env.PUBLIC_URL}/assets/projects/net-zero-city/351.png`,
];

// Older studies, dissertation, academic + early conceptual work
export const ARCHIVE_PROJECTS = [
  {
    label: "Bean & Leaf Cafe",
    labelAr: "مقهى Bean & Leaf",
    tag: "Visuals",
    year: "2019",
    img: beanAndLeafImages[0],
    images: beanAndLeafImages,
    description: "Cafe + bakery study exploring a compact hospitality interior across two levels. The project uses warm timber surfaces, a calm minimalist palette, and careful seating arrangements to create a small urban cafe that feels open, readable, and welcoming.",
    descriptionAr: "دراسة لمقهى ومخبز على مستويين، تستكشف مساحة ضيافة صغيرة ومضغوطة. يعتمد المشروع على أسطح خشبية دافئة ولوحة هادئة وترتيب جلوس واضح لصناعة مقهى حضري مفتوح وسهل القراءة ومرحّب.",
  },
  {
    label: "Artist Hub",
    labelAr: "مركز الفنانين",
    tag: "Visuals",
    year: "2020",
    img: artistHubImages[0],
    images: artistHubImages,
    description: "Modular creative hub designed as a flexible framework for artists, musicians, makers, and performers. The proposal tests a raised, lightweight structure with adaptable studio spaces, performance areas, and shared facilities that can shift between individual work and public events.",
    descriptionAr: "مركز إبداعي مرن مصمم كإطار قابل للتكيّف للفنانين والموسيقيين والصنّاع وفناني الأداء. يختبر المشروع بنية خفيفة ومرتفعة تضم استوديوهات قابلة للتغيير ومساحات أداء ومرافق مشتركة تتحول بين العمل الفردي والفعاليات العامة.",
  },
  {
    label: "Net Zero City",
    labelAr: "مدينة صافي الصفر",
    tag: "Research",
    year: "2020",
    img: netZeroCityImages[0],
    images: netZeroCityImages,
    description: "Department for Net Zero City proposes a public-facing environmental building where carbon reduction is made visible through architecture. The scheme combines timber construction, recycled cladding, daylight-responsive louvers, planted surfaces, and public circulation to turn sustainability from a hidden technical system into an educational civic experience.",
    descriptionAr: "يقترح مشروع Department for Net Zero City مبنى بيئيًا عامًا يجعل تقليل البصمة الكربونية مرئيًا من خلال العمارة. يجمع التصميم بين البناء الخشبي والكسوة المعاد تدويرها واللوفرات المستجيبة للضوء والأسطح المزروعة ومسارات الحركة العامة، ليحوّل الاستدامة من نظام تقني مخفي إلى تجربة مدنية وتعليمية.",
  },
  {
    label: "P.P.H. Housing",
    tag: "Visuals",
    year: "2021",
    img: "https://images.unsplash.com/photo-1448630360428-65456885c650?auto=format&fit=crop&w=600&q=70",
    description: "Post-Pandemic Housing — a shared-living typology proposed for Bath, addressing affordability and isolation.",
  },
  {
    label: "Outdoor Spaces · Diss.",
    tag: "Academic",
    year: "2022",
    img: null,
    description: "Dissertation: the role of balconies and outdoor space in high-rise dwellings during times of crisis.",
  },
  {
    label: "Libraries as Carrier Bags",
    tag: "Academic",
    year: "2022",
    img: null,
    description: "Final-year project at BCU — Wolverhampton Central Library reimagined as a vessel for memory.",
  },
  {
    label: "Post-Work Library",
    tag: "Experiments",
    year: "2121",
    img: null,
    description: "Speculative library set in 2121 — an archive of jobs lost to automation, guided by a Keeper of Narratives.",
  },
  {
    label: "Dissolving Brick",
    tag: "Experiments",
    year: "2022",
    img: null,
    description: "Material experiment exploring time as a weathering agent — bricks that dissolve as they age.",
  },
  {
    label: "Gravitational Bend",
    tag: "Models",
    year: "2022",
    img: "https://images.pexels.com/photos/35630641/pexels-photo-35630641.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Physical model — memory bending under its own gravity, made for the Carrier Bags project.",
  },
  {
    label: "Wolverhampton Axon.",
    tag: "Models",
    year: "2022",
    img: "https://images.unsplash.com/photo-1749563558879-9541b31b7e4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwcGh5c2ljYWwlMjBtb2RlbHxlbnwwfHx8fDE3NzgzNTYzNjZ8MA&ixlib=rb-4.1.0&q=85",
    description: "Axonometric study of Wolverhampton Central Library — preparatory model for the dissertation project.",
  },
  {
    label: "Plan study",
    tag: "Sketches",
    year: "2021",
    img: null,
    description: "Hand-drawn plan investigation from the P.P.H. housing study.",
  },
  {
    label: "Section study",
    tag: "Sketches",
    year: "2020",
    img: null,
    description: "Sectional sketch from the Net Zero City project — exploring stack ventilation and daylight.",
  },
  {
    label: "Intervention map",
    tag: "Experiments",
    year: "2022",
    img: null,
    description: "An on-site intervention recording how strangers negotiate decisions — fieldwork for Carrier Bags.",
  },
  {
    label: "Work-Placement Unit",
    tag: "Sketches",
    year: "2021",
    img: null,
    description: "Accessible residential unit designed for a disabled occupant — wide doors, smart-home, roll-in shower.",
  },
];

const Doodle = ({ idx }) => {
  const variants = [
    <svg viewBox="0 0 60 60" key="a"><rect x="8" y="14" width="44" height="32" fill="none" stroke="currentColor" strokeWidth="0.8" /><line x1="8" y1="22" x2="52" y2="22" stroke="currentColor" strokeWidth="0.6" /></svg>,
    <svg viewBox="0 0 60 60" key="b"><circle cx="30" cy="30" r="18" fill="none" stroke="currentColor" strokeWidth="0.8" /><line x1="12" y1="30" x2="48" y2="30" stroke="currentColor" strokeWidth="0.6" strokeDasharray="2 2"/></svg>,
    <svg viewBox="0 0 60 60" key="c"><polygon points="30,8 52,46 8,46" fill="none" stroke="currentColor" strokeWidth="0.8"/></svg>,
    <svg viewBox="0 0 60 60" key="d"><path d="M6 44 L18 22 L30 36 L42 18 L54 30" fill="none" stroke="currentColor" strokeWidth="0.9"/></svg>,
    <svg viewBox="0 0 60 60" key="e"><path d="M10 36 L30 26 L50 36 L30 46 Z" fill="none" stroke="currentColor" strokeWidth="0.8" /><path d="M10 36 V20 L30 10 L50 20 V36" fill="none" stroke="currentColor" strokeWidth="0.8" /></svg>,
    <svg viewBox="0 0 60 60" key="f"><path d="M12 14 H48 V46 H12 Z M12 24 H48 M22 14 V46 M34 14 V46" fill="none" stroke="currentColor" strokeWidth="0.7"/></svg>,
  ];
  return <div className="text-ink/70 w-full h-full grid place-items-center">{variants[idx % variants.length]}</div>;
};

const projectId = (project) => project.id || `archive-${(project.label || project.title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

export const ArchiveContent = ({ onOpen, customProjects = [], deletedProjectIds = [] }) => {
  const { lang, t } = useLanguage();
  const [active, setActive] = useState("All");
  const archiveItems = useMemo(() => {
    const deleted = new Set(deletedProjectIds);
    const customIds = new Set(customProjects.map(projectId));
    return [
      ...customProjects,
      ...ARCHIVE_PROJECTS.filter((project) => !customIds.has(projectId(project))),
    ].filter((project) => !deleted.has(projectId(project)));
  }, [customProjects, deletedProjectIds]);
  const items = useMemo(
    () => (active === "All" ? archiveItems : archiveItems.filter((a) => a.tag === active || a.category === active)),
    [active, archiveItems]
  );

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {TAGS.map((tag) => (
          <button
            key={tag.id}
            data-testid={`archive-tag-${tag.id.toLowerCase()}`}
            onClick={() => setActive(tag.id)}
            className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border transition ${
              active === tag.id
                ? "border-ink bg-ink text-paper"
                : "border-[var(--paper-edge)] text-ink-soft hover:border-ink/60 hover:text-ink"
            }`}
          >
            {t(tag.labelKey)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {items.map((it, i) => (
          <button
            key={projectId(it)}
            data-testid={`archive-item-${i}`}
            onClick={() => onOpen({
              title: localized(it, "label", lang) || it.title,
              kind: t("archiveKind"),
              year: it.year,
              category: t((it.tag || it.category || "visuals").toLowerCase()) || it.category,
              image: it.img || it.image || undefined,
              images: it.images,
              no: i + 100,
              description: localized(it, "description", lang),
              details: it.details?.length ? it.details : [`${t("type")} — ${t((it.tag || it.category || "visuals").toLowerCase())}`, `${t("year")} — ${it.year}`, `${t("stage")} — ${t("archiveKind")}`, `${t("author")} — Abdulrahman Alarifi`],
            })}
            className="relative aspect-square bg-[var(--paper)] border border-[var(--paper-edge)] hover:border-ink/40 transition overflow-hidden group"
            style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 0.6}deg)` }}
          >
            {it.img || it.image ? (
              <img src={it.img || it.image} alt={localized(it, "label", lang) || it.title} className="w-full h-full object-cover" />
            ) : (
              <Doodle idx={i} />
            )}
            <span className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-paper/85 text-[9px] font-mono uppercase tracking-[0.15em] text-ink truncate">
              {localized(it, "label", lang) || it.title}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
